import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ButtonPrimary } from "../../components/Button";
import GoBack from "../../components/GoBack";
import ModalComponent from "../../components/Modals/modal";
import CreateStoreDrawer from "./CreateStoreDrawer";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  fetchVariantsbyProductID,
  addInventoryStock,
} from "../../redux/Inventory/inventoryActions";

const AddInventoryPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { addStockLoading } = useSelector((state) => state.inventory);
  const [activeVariant, setActiveVariant] = useState(0);
  const [showEditVariantModal, setShowEditVariantModal] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [showCreateStoreDrawer, setShowCreateStoreDrawer] = useState(false);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState([]);
  const [variantFormData, setVariantFormData] = useState({});

  // Shimmer loader component for variants
  const VariantShimmer = () => (
    <div className="animate-pulse">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex px-4 py-3 items-center justify-between border-b border-[#E1E6E1]"
        >
          <div className="flex items-center gap-1">
            <div className="h-6 bg-gray-200 rounded w-16"></div>
            <span className="font-semibold text-lg text-[#E1E6E1]">/</span>
            <div className="h-6 bg-gray-200 rounded w-12"></div>
          </div>
          <div className="h-6 bg-gray-200 rounded w-8"></div>
        </div>
      ))}
    </div>
  );

  // Function to calculate profit and margin
  const calculateProfitAndMargin = (sellingPrice, costPrice) => {
    const profit = sellingPrice - costPrice;
    const margin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;
    return {
      profit: profit,
      margin: margin.toFixed(2),
      status: profit >= 0 ? "positive" : "negative",
    };
  };

  // Fetch product details on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const productId = searchParams.get("product_id");

    if (productId) {
      setLoading(true);
      dispatch(fetchVariantsbyProductID({ productId: productId }))
        .then((res) => {
          if (res.type === "fetchVariantsbyProductID/fulfilled") {
            const productData = res.payload.data;
            setProductData(productData);

            // Set variants directly from API data
            if (productData.variants && productData.variants.length > 0) {
              setVariants(productData.variants);
              console.log("Product variants loaded:", productData.variants);
            }

            console.log("Product details loaded:", productData);
          } else if (res.type === "fetchVariantsbyProductID/rejected") {
            console.error("Failed to load product details:", res.payload);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching product details:", error);
          setLoading(false);
        });
    }
  }, [dispatch, location.search]);

  // Function to update variant form fields and recalculate profit/margin
  const updateVariantField = (variantIndex, field, value) => {
    const variantId = variants[variantIndex]?.id;
    if (!variantId) return;

    setVariantFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      if (!newFormData[variantId]) {
        newFormData[variantId] = {
          costPrice: 0,
          previousCostPrice: 0,
          marketPrice: 0,
          previousSellingPrice: 0,
          sellingPrice: 0,
          profit: 0,
          margin: 0,
          quantity: 0,
          expiryDate: "",
          productThreshold: "<10",
          sku: "",
        };
      }

      const variantData = { ...newFormData[variantId] };

      // Handle text fields differently from numeric fields
      if (
        field === "expiryDate" ||
        field === "productThreshold" ||
        field === "sku"
      ) {
        variantData[field] = value;
      } else {
        variantData[field] = parseFloat(value) || 0;
      }

      // Recalculate profit and margin only when selling price is greater than 0
      if (field === "costPrice" || field === "sellingPrice") {
        if (variantData.sellingPrice > 0) {
          const calculation = calculateProfitAndMargin(
            variantData.sellingPrice,
            variantData.costPrice
          );
          variantData.profit = calculation.profit;
          variantData.margin = calculation.margin;
        } else {
          // Reset to 0 when no selling price
          variantData.profit = 0;
          variantData.margin = 0;
        }
      }

      newFormData[variantId] = variantData;
      return newFormData;
    });
  };

  // Get form data for a specific variant
  const getVariantFormData = (variantIndex) => {
    const variantId = variants[variantIndex]?.id;
    return (
      variantFormData[variantId] || {
        costPrice: 0,
        previousCostPrice: 0,
        marketPrice: 0,
        previousSellingPrice: 0,
        sellingPrice: 0,
        profit: 0,
        margin: 0,
        quantity: 0,
        expiryDate: "",
        productThreshold: "<10",
        sku: "",
      }
    );
  };

  // Function to prepare payload for Add Stock API
  const prepareAddStockPayload = () => {
    const payload = variants
      .map((variant) => {
        const formData = getVariantFormData(variants.indexOf(variant));

        // Only include variants that have form data with quantity > 0
        if (formData.quantity > 0) {
          return {
            variant_id: variant.id,
            quantity: parseInt(formData.quantity) || 0,
            cost_price: parseFloat(formData.costPrice) || 0,
            selling_price: parseFloat(formData.sellingPrice) || 0,
            market_price: parseFloat(formData.marketPrice) || 0,
            sku: formData.sku || variant.sku || "",
            expiry_date: formData.expiryDate || null,
            reorder_point:
              parseInt(formData.productThreshold?.replace(/[<>]/g, "")) || 10,
            notes: `Stock added for ${variant.variant_data?.type} ${variant.variant_data?.value}${variant.variant_data?.unit}`,
          };
        }
        return null;
      })
      .filter(Boolean); // Remove null entries

    return payload;
  };

  // Function to validate form data before submission
  const validateStockData = () => {
    const errors = [];
    let hasValidData = false;

    variants.forEach((variant, index) => {
      const formData = getVariantFormData(index);

      if (formData.quantity > 0) {
        hasValidData = true;

        if (!formData.costPrice || formData.costPrice <= 0) {
          errors.push(
            `Cost price is required for ${variant.variant_data?.type} ${variant.variant_data?.value}${variant.variant_data?.unit}`
          );
        }

        if (!formData.sellingPrice || formData.sellingPrice <= 0) {
          errors.push(
            `Selling price is required for ${variant.variant_data?.type} ${variant.variant_data?.value}${variant.variant_data?.unit}`
          );
        }
      }
    });

    if (!hasValidData) {
      errors.push("Please add quantity for at least one variant");
    }

    return { isValid: errors.length === 0, errors };
  };

  // Handle Add Stock button click
  const handleAddStock = () => {
    const validation = validateStockData();

    if (!validation.isValid) {
      // Show first error
      console.error("Validation errors:", validation.errors);
      alert(validation.errors[0]); // You can replace with a better notification system
      return;
    }

    const payload = prepareAddStockPayload();

    if (payload.length === 0) {
      alert("No valid stock data to submit");
      return;
    }

    console.log("Submitting stock data:", payload);

    dispatch(addInventoryStock({ variants_stock: payload }))
      .then((res) => {
        if (res.type === "addInventoryStock/fulfilled") {
          // Reset form data on success
          setVariantFormData({});
          navigate("/product-inventory");
        }
      })
      .catch((error) => {
        console.error("Error adding stock:", error);
      });
  };

  const allVariantsSelected = selectedVariants.length === variants.length;

  const toggleSelectAllVariants = () => {
    if (allVariantsSelected) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants([...variants]);
    }
  };

  const toggleVariantSelection = (variant) => {
    setSelectedVariants((prevSelected) => {
      const exists = prevSelected.some((v) => v.id === variant.id);

      if (exists) {
        return prevSelected.filter((v) => v.id !== variant.id);
      } else {
        return [...prevSelected, variant];
      }
    });
  };

  return (
    <>
      <div className="mb-3">
        <GoBack />
      </div>
      <div
        className={`bg-white border flex justify-between items-center border-[#E1E6E1] p-5 rounded-[8px]`}
      >
        <div className="">
          <p className="text-primary text-lg font-semibold leading-6">
            {`Add Stock${productData ? `- ${productData.product.name}` : ""}`}
          </p>
        </div>

        <div>
          <button
            type="submit"
            disabled={addStockLoading}
            onClick={handleAddStock}
            className="bg-primary 
                  disabled:bg-[#ABB6AC] 
                  rounded-lg w-full py-[10px] px-5 text-white font-medium hover:bg-[#24752B] transition-all"
          >
            {addStockLoading ? "Adding Stock..." : "Add Stock"}
          </button>
        </div>
      </div>
      <div className="mt-4 flex min-h-[800px] border border-[#E1E6E1] rounded-[8px]">
        {/* Left Section */}
        <div className="w-[35%] border-r border-[#E1E6E1] bg-white">
          <div className="flex px-4 py-3 items-center justify-between border-b border-[#E1E6E1]">
            <div className="flex items-center gap-1">
              <p className="text-[#2E332F] font-semibold text-sm">Variants</p>
              <div className="w-fit px-1 text-center bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] font-semibold text-xs rounded-[4px]">
                {variants.length}
              </div>
            </div>
            <button
              onClick={() => setShowEditVariantModal(true)}
              className="text-[#2B8C34] text-sm font-semibold"
            >
              Edit Variant
            </button>
          </div>
          {/* The Variants */}
          {loading ? (
            <VariantShimmer />
          ) : variants.length > 0 ? (
            variants.map((variant, index) => (
              <div
                key={variant.id}
                onClick={() => {
                  setActiveVariant(index);
                }}
                className={`flex px-4 py-3 items-center justify-between border-b border-[#E1E6E1] cursor-pointer ${
                  activeVariant === index
                    ? "bg-[#DDEFE2] text-[#1A3D2F]"
                    : "bg-white text-[#5F6D60]"
                }`}
              >
                <div className="text-[#5F6D60] flex items-center gap-1 font-semibold text-base">
                  <p
                    className={`border w-fit py-1 px-1
                      ${
                        activeVariant === index
                          ? "border-[#2B8C34] text-[#2B8C34] bg-white"
                          : "border-[#E1E6E1] text-[#5F6D60]"
                      }
                    text-sm rounded-[4px] font-semibold`}
                  >
                    {variant.variant_data?.type}
                  </p>
                  <span className="font-semibold text-lg text-[#5F6D60]">
                    /
                  </span>
                  <p
                    className={`border 
                w-fit py-1 px-1 bg-#fff 
                  ${
                    activeVariant === index
                      ? "border-[#2B8C34] text-[#2B8C34] bg-white"
                      : "border-[#E1E6E1] text-[#5F6D60]"
                  }
                  text-sm rounded-[4px] font-semibold`}
                  >
                    {variant.variant_data?.value} {variant.variant_data?.unit}
                  </p>
                </div>
                <div className="w-fit px-1 py-1 text-center bg-[#FFFFFF] text-[#5F6D60] text-sm font-semibold border border-[#E1E6E1] rounded-[4px]">
                  {variant?.current_stock || 0}
                </div>
              </div>
            ))
          ) : (
            <div className="flex px-4 py-8 items-center justify-center text-[#96A397]">
              <p>No variants available</p>
            </div>
          )}
        </div>
        {/* Right Section */}
        {loading ? (
          <div className="w-full bg-[#F7F7F7] flex items-center justify-center">
            <div className="animate-pulse w-full p-8">
              <div className="h-8 bg-gray-200 rounded mb-4"></div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
              </div>
              <div className="h-32 bg-gray-200 rounded mb-4"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : variants.length > 0 ? (
          variants.map((variant, index) =>
            index === activeVariant ? (
              <div
                key={`${variant.variant_data?.value}-${variant.variant_data?.unit}`}
                className="w-full bg-[#F7F7F7]"
              >
                <div className=" px-4 py-3  bg-white items-center border-b border-[#E1E6E1]">
                  <div className="flex items-center gap-1">
                    <p className="text-[#2E332F] font-semibold text-sm">
                      Variant Cost Price
                    </p>
                  </div>
                </div>

                <div className="w-full py-4 px-4  flex gap-4">
                  <div className="bg-[#F7F7F7] w-1/2">
                    <div className="border border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
                      <p className="text-sm text-[#3E473F] font-semibold">
                        Cost Price
                      </p>
                      <div className="w-fit px-1 py-1 text-center text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                        Prev. CP ₦ {getVariantFormData(index).previousCostPrice}
                      </div>
                    </div>
                    <div className="px-4 py-5 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
                      <div className="relative">
                        {/* <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5F6D60] font-medium text-xl">
                        ₦
                      </span> */}
                        <input
                          type="number"
                          onWheel={(e) => e.target.blur()}
                          value={getVariantFormData(index).costPrice || ""}
                          onChange={(e) =>
                            updateVariantField(
                              index,
                              "costPrice",
                              e.target.value
                            )
                          }
                          className="w-full text-2xl placeholder:text-[#96A397] font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                          placeholder="₦ 0.00"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F7F7F7] w-1/2">
                    <div className="border min-h-[44px] border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]  flex justify-between items-center">
                      <p className="text-sm text-[#3E473F] font-semibold">
                        Market Price
                      </p>
                    </div>
                    <div className="px-4 py-5 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
                      <div className="relative">
                        {/* <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5F6D60] font-medium text-xl">
                        ₦
                      </span> */}
                        <input
                          type="number"
                          value={getVariantFormData(index).marketPrice || ""}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) =>
                            updateVariantField(
                              index,
                              "marketPrice",
                              e.target.value
                            )
                          }
                          className="w-full text-2xl font-semibold text-[#3E473F] placeholder:text-[#96A397] bg-transparent border-none focus:outline-none"
                          placeholder="₦ 0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex px-4 py-3 items-center justify-between border-b border-t bg-white border-[#E1E6E1]">
                  <div className="flex items-center gap-1">
                    <p className="text-[#2E332F] font-semibold text-sm">
                      {variant.store} Selling Price and Inventory
                    </p>
                  </div>
                  <div className="w-fit px-1 py-1 text-center bg-[#F7F7F7] text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                    0 Products
                  </div>
                </div>
                {/* Store Details */}
                <div className="bg-[#F7F7F7] min-h-[600px] overflow-y-auto">
                  <div className="bg-[#F7F7F7] p-4 ">
                    <div className="border border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
                      <p className="text-sm text-[#3E473F] font-semibold">
                        Selling Price
                      </p>
                      <div className="w-fit px-1 py-1 text-center text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                        Prev. SP ₦{" "}
                        {getVariantFormData(index).previousSellingPrice}
                      </div>
                    </div>
                    <div className="px-4 py-5 border-r border-l border-[#E1E6E1] bg-white">
                      <div className="relative">
                        {/* <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5F6D60] font-medium text-xl">
                        ₦
                      </span> */}
                        <input
                          type="number"
                          value={getVariantFormData(index).sellingPrice || ""}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) =>
                            updateVariantField(
                              index,
                              "sellingPrice",
                              e.target.value
                            )
                          }
                          className="w-full text-2xl font-semibold placeholder:text-[#96A397] text-[#3E473F] bg-transparent border-none focus:outline-none"
                          placeholder="₦ 0.00"
                        />
                      </div>
                    </div>
                    {/* Profit Margin Details */}

                    <div className="w-full flex border border-[#E1E6E1] rounded-bl-[8px] rounded-br-[8px]">
                      <div
                        className={`${
                          getVariantFormData(index).sellingPrice > 0
                            ? getVariantFormData(index).profit < 0
                              ? "bg-[#B92020]"
                              : "bg-[#2B8C34]"
                            : "bg-white"
                        } flex rounded-bl-[8px] gap-2 items-center py-2 px-4 w-1/2`}
                      >
                        <p
                          className={`${
                            getVariantFormData(index).sellingPrice > 0
                              ? "text-white"
                              : "text-[#5F6D60]"
                          } font-semibold text-sm`}
                        >
                          {getVariantFormData(index).sellingPrice > 0 &&
                          getVariantFormData(index).profit < 0
                            ? "Loss"
                            : "Profit"}
                          :{" "}
                          <span
                            className={`${
                              getVariantFormData(index).sellingPrice > 0
                                ? "text-white"
                                : "text-[#96A397]"
                            }`}
                          >
                            ₦{" "}
                            {getVariantFormData(index).sellingPrice > 0
                              ? Math.abs(getVariantFormData(index).profit)
                              : "0.00"}
                          </span>
                        </p>
                        <span
                          className={`${
                            variant.sellingPrice > 0
                              ? "text-[#F7F7F7]"
                              : "text-[#E1E6E1]"
                          }`}
                        >
                          |
                        </span>
                        <p
                          className={`${
                            getVariantFormData(index).sellingPrice > 0
                              ? "text-white"
                              : "text-[#5F6D60]"
                          } font-semibold text-sm`}
                        >
                          Margin:{" "}
                          <span
                            className={`${
                              getVariantFormData(index).sellingPrice > 0
                                ? "text-white"
                                : "text-[#96A397]"
                            }`}
                          >
                            {getVariantFormData(index).sellingPrice > 0
                              ? getVariantFormData(index).margin
                              : "0"}
                            %
                          </span>
                        </p>
                      </div>
                      <div
                        className={`${
                          getVariantFormData(index).sellingPrice > 0
                            ? getVariantFormData(index).profit < 0
                              ? "bg-[#FEF8D8] text-[#D8A600]"
                              : getVariantFormData(index).sellingPrice >
                                getVariantFormData(index).marketPrice
                              ? "bg-[#F2FBF3] text-[#25702C]"
                              : "bg-[#FEF8D8] text-[#D8A600]"
                            : "bg-white text-white"
                        } flex rounded-br-[8px] border-l border-[#E1E6E1] justify-center items-center text-sm font-semibold py-2 px-4 w-1/2`}
                      >
                        {getVariantFormData(index).sellingPrice > 0 ? (
                          <p>
                            {getVariantFormData(index).marketPrice ===
                            getVariantFormData(index).sellingPrice
                              ? "Same Market Price"
                              : `₦${Math.abs(
                                  getVariantFormData(index).sellingPrice -
                                    getVariantFormData(index).marketPrice
                                )} ${
                                  getVariantFormData(index).sellingPrice >
                                  getVariantFormData(index).marketPrice
                                    ? "Higher"
                                    : "Lower"
                                } Than Market Price`}
                          </p>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Quantity */}
                  <div className="bg-[#F7F7F7] p-4">
                    <div className="border border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
                      <p className="text-sm text-[#3E473F] font-semibold">
                        Quantity
                      </p>
                    </div>
                    <div className="p-4 border-r border-b border-l border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
                      <input
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        value={getVariantFormData(index).quantity || ""}
                        onChange={(e) =>
                          updateVariantField(index, "quantity", e.target.value)
                        }
                        className="w-full py-2 text-2xl font-semibold text-[#3E473F] placeholder:text-[#96A397] bg-transparent border-none focus:outline-none"
                        placeholder="00"
                      />
                    </div>
                  </div>
                  {/* Inventory Info */}
                  <div className="bg-[#F7F7F7] p-4">
                    <div className="border border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
                      <p className="text-sm text-[#3E473F] font-semibold">
                        Inventory Info
                      </p>
                    </div>
                    <div className="p-4 text-2xl border border-[#E1E6E1] text-[#3E473F] grid grid-cols-3 gap-8  font-semibold bg-white rounded-bl-[8px] rounded-br-[8px]">
                      {/* Expiry Date */}
                      <div>
                        <p className="text-sm text-[#3E473F] font-medium">
                          Expiry Date
                        </p>
                        <div className="border border-[#E1E6E1] w-full rounded-lg px-2 mt-1">
                          <input
                            type="date"
                            value={getVariantFormData(index).expiryDate || ""}
                            onChange={(e) =>
                              updateVariantField(
                                index,
                                "expiryDate",
                                e.target.value
                              )
                            }
                            className="w-full text-[#3E473F] text-sm font-semibold px-2 py-2 bg-transparent border-none focus:outline-none"
                          />
                        </div>
                      </div>
                      {/* Sku */}
                      <div className="">
                        <p className="text-sm text-[#3E473F] font-medium">
                          SKU
                        </p>
                        <div className="border border-[#E1E6E1] w-full rounded-lg px-2 mt-1">
                          <input
                            type="text"
                            value={
                              getVariantFormData(index).sku || variant.sku || ""
                            }
                            onChange={(e) =>
                              updateVariantField(index, "sku", e.target.value)
                            }
                            className="w-full text-[#3E473F] text-sm font-semibold px-2 py-2 bg-transparent border-none focus:outline-none"
                            placeholder="Enter SKU"
                          />
                        </div>
                      </div>
                      {/* Product Threshold(Units) */}
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-[#3E473F] font-medium">
                            Product Threshold (Units)
                          </p>

                          <div className="relative group inline-block">
                            <p className="text-black cursor-pointer text-[10px] font-semibold border border-gray-400 bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center ml-1">
                              i
                            </p>

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[180px] rounded-md bg-gray-100 text-black text-xs font-medium px-3 py-2 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                              Minimum quantity before product is flagged as low
                              in stock.
                              {/* Little pointer triangle */}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-100 rotate-45 -mt-1 shadow-md"></div>
                            </div>
                          </div>
                        </div>

                        <div className="border border-[#E1E6E1] w-full rounded-lg px-2 mt-1">
                          <input
                            type="text"
                            value={
                              getVariantFormData(index).productThreshold || ""
                            }
                            onChange={(e) =>
                              updateVariantField(
                                index,
                                "productThreshold",
                                e.target.value
                              )
                            }
                            className="w-full text-[#3E473F] text-sm font-semibold px-2 py-2 bg-transparent border-none focus:outline-none"
                            placeholder="00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          )
        ) : (
          <div className="w-full bg-[#F7F7F7] flex items-center justify-center">
            <div className="p-8 text-center text-[#96A397]">
              <p className="text-sm">No variant selected</p>
            </div>
          </div>
        )}
      </div>
      {/* Edit Variant Modal */}
      <ModalComponent
        show={showEditVariantModal}
        showModal={() => setShowEditVariantModal(!showEditVariantModal)}
        title="Edit Variant"
        subtitle="Select the variant(s) to edit"
      >
        {/* Variant List */}
        <div className="flex flex-col my-3 bg-white rounded-[8px] border border-[#E1E6E1] overflow-y-auto h-fit">
          <div className="flex justify-between items-center  border-b px-3 py-3">
            <label className="flex items-center gap-2 text-[#5F6D60] text-base font-semibold">
              <input
                className="w-4 h-4 accent-[#2B8C34] text-sm font-semibold"
                type="checkbox"
                checked={allVariantsSelected}
                onChange={toggleSelectAllVariants}
              />
              Select All
            </label>

            <p className="text-sm font-semibold text-[#2B8C34]">
              {selectedVariants.length} selected
            </p>
          </div>
          {loading ? (
            <VariantShimmer />
          ) : variants.length > 0 ? (
            variants.map((variant, index) => (
              <label
                className="flex items-center gap-2 border-b px-3 py-3 text-[#5F6D60] "
                key={variant.id || index}
              >
                <input
                  className="w-4 h-4 accent-[#2B8C34] text-sm font-semibold"
                  type="checkbox"
                  checked={selectedVariants.some((v) => v.id === variant.id)}
                  onChange={() => {
                    toggleVariantSelection(variant);
                  }}
                />
                <div>
                  <div className="text-[#5F6D60] flex items-center gap-1 font-semibold text-base">
                    <p className="border w-fit py-0.5 px-1 bg-[#EDF7EE] border-[#E1E6E1] rounded-md font-semibold">
                      {variant.variant_data?.type}
                    </p>
                    <span className="font-semibold text-lg text-[#5F6D60]">
                      /
                    </span>
                    <p className="border w-fit py-0.5 px-1 bg-[#EDF7EE] border-[#E1E6E1] rounded-md font-semibold">
                      {variant.variant_data?.value} {variant.variant_data?.unit}
                    </p>
                  </div>
                </div>
              </label>
            ))
          ) : (
            <div className="flex px-4 py-8 items-center justify-center text-[#96A397]">
              <p>No variants available</p>
            </div>
          )}
          <div className=" items-center px-4 py-2 text-[#5F6D60]">
            <Link to={"/"} className="text-[#2B8C34] text-sm font-semibold">
              Go to Catalogue
            </Link>
          </div>
        </div>

        <div className="flex justify-end">
          <ButtonPrimary
            onclick={() => setShowEditVariantModal(false)}
            children={"Continue"}
          />
        </div>
      </ModalComponent>
      {/* Create Store Drawer */}
      <CreateStoreDrawer
        showCreateStoreDrawer={showCreateStoreDrawer}
        setShowCreateStoreDrawer={setShowCreateStoreDrawer}
      />
    </>
  );
};

export default AddInventoryPage;
